import envService from '@/services/env.service';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { processAndCollectAllParams, getFbc, getFbp } from 'meta-capi-param-builder-clientjs';
import { PropsWithChildren, createContext, useContext, useRef } from 'react';

type AmplitudeValue = any;

const AmplitudeContext = createContext<AmplitudeValue>(amplitude);

export function useAmplitude() {
  const context = useContext(AmplitudeContext);

  if (!context) throw new Error('useAmplitude must be used within a AmplitudeProvider');

  return context;
}

function getDeviceIdFromUrlOrCookie(): string | undefined {
  // Priority 1: URL parameter (from marketing site)
  const urlParams = new URLSearchParams(window.location.search);
  const urlDeviceId = urlParams.get('amp_device_id');
  if (urlDeviceId) {
    // Store in cookie for future page loads with matching domain configuration
    // Use .catchbio.com domain in production for cross-subdomain sharing
    const isProduction = window.location.hostname.endsWith('.catchbio.com');
    const domainAttr = isProduction ? '; domain=.catchbio.com' : '';
    document.cookie = `amp_device_id=${urlDeviceId}; path=/; max-age=${60 * 60 * 24 * 365}; secure; samesite=lax${domainAttr}`;
    return urlDeviceId;
  }

  // Priority 2: Cookie (cross-domain or previously stored)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'amp_device_id') {
      return value;
    }
  }

  // Priority 3: Let Amplitude SDK generate new ID
  return undefined;
}

export function AmplitudeProvider({ children }: PropsWithChildren) {
  // Read device ID from URL parameter or cookie
  const deviceId = getDeviceIdFromUrlOrCookie();

  // Guard plugin init so it only runs once across re-renders.
  const sessionReplayAdded = useRef(false);
  if (!sessionReplayAdded.current) {
    sessionReplayAdded.current = true;

    const amplitudeSampleRate = envService.getEnv().VITE_AMPLITUDE_SAMPLE_RATE;
    // If the sample rate is not set, use remote configuration or default is 0
    const sampleRate = amplitudeSampleRate && amplitudeSampleRate !== '' ? Number(amplitudeSampleRate) : undefined;
    // All iOS browsers use WebKit and are subject to ITP/storage eviction (7-day rule,
    // storage pressure, user data clearing) — applies to Safari, DuckDuckGo, Edge, Brave, etc.
    // Firefox on Android also aggressively clears IndexedDB under Enhanced Tracking Protection.
    // Use in-memory storage for these browsers to avoid unhandled rejections and silent replay loss.
    const useMemoryStorage =
      (/iP(hone|od|ad)/.test(navigator.userAgent) && /WebKit/.test(navigator.userAgent)) ||
      (/Android/.test(navigator.userAgent) && /Firefox/.test(navigator.userAgent));
    const sessionReplayTracking = sessionReplayPlugin({
      sampleRate,
      // Use proxy URL if configured, empty string falls back to Amplitude default
      trackServerUrl: envService.getEnv().VITE_AMPLITUDE_SR_PROXY_URL || '',
      ...(useMemoryStorage ? { storeType: 'memory' as const } : {}),
    });
    try {
      amplitude.add(sessionReplayTracking);
    } catch {
      // Session replay unavailable — non-fatal, continue without it
    }
  }

  amplitude.init(envService.getEnv().VITE_AMPLITUDE_API_KEY, {
    deviceId: deviceId,
    autocapture: {
      attribution: {
        resetSessionOnNewCampaign: true,
      },
      pageViews: false,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },
    // Use proxy URL if configured, empty string falls back to Amplitude default
    serverUrl: envService.getEnv().VITE_AMPLITUDE_PROXY_URL || '',
  });

  // Capture Meta _fbc and _fbp for Conversions API attribution.
  // Uses Meta's official param builder to read existing cookies OR create them
  // from the fbclid URL param (handles Pixel-blocked browsers like Brave/Safari).
  // Values are set as Amplitude user properties so the Amplitude → Meta Pixel
  // data destination forwards them with correct creationTime.
  processAndCollectAllParams();
  const fbc = getFbc();
  const fbp = getFbp();

  if (fbc || fbp) {
    const identify = new amplitude.Identify();
    if (fbc) identify.set('fbc', fbc);
    if (fbp) identify.set('fbp', fbp);
    amplitude.identify(identify);
  }

  return <AmplitudeContext.Provider value={amplitude}>{children}</AmplitudeContext.Provider>;
}
